/**
 * Brand-name → domain inference, used to pre-fill a site's primary URL when a
 * user (or an imported file) gives only a name. Runs **entirely on-device** —
 * no network call, no third-party lookup — so it never leaks which sites a
 * user is adding (consistent with the zero-knowledge / no-tracking posture).
 *
 * Resolution order:
 *   1. The input already looks like a domain/URL → return its host.
 *   2. Exact hit in the curated BRAND_DOMAINS map (covers common brands and
 *      aliases whose domain differs from a naive slug, e.g. "amex" →
 *      americanexpress.com, "x"/"twitter" → x.com).
 *   3. Heuristic fallback: slugify and append ".com" ("Bank of America" →
 *      bankofamerica.com). A guess — callers only ever use it to fill an
 *      EMPTY field, never to overwrite, and the user can edit it.
 *
 * Returns a bare host (e.g. "cnn.com"); `ensureUrlScheme` in `./url` adds the
 * scheme at launch/parse time, matching how users already store bare hosts.
 */

import { tryParseUrl } from './url';

/**
 * Curated brand → apex-domain map — **exceptions only**. An entry belongs here
 * ONLY when the `<slug>.com` heuristic below would get it wrong, i.e. one of:
 *   - an alias/abbreviation ("amex" → americanexpress.com, "fb" → facebook.com)
 *   - a non-`.com` TLD ("proton" → proton.me, "irs" → irs.gov, "twitch" → twitch.tv)
 *   - the brand's domain isn't its name ("twitter" → x.com, "comcast" → xfinity.com)
 *   - a sub-domain product ("google drive" → drive.google.com)
 *   - a name too short for the heuristic's 2-char floor ("x" → x.com)
 *
 * Do NOT add a brand the heuristic already resolves (e.g. cnn, netflix, github,
 * chase, "bank of america" → bankofamerica.com) — the no-redundant-entry test
 * in brandDomain.test.ts will fail. Covers the exceptions among the top US
 * consumer sites; the heuristic covers everything else.
 */
export const BRAND_DOMAINS: Record<string, string> = {
  // ── Social / messaging ──
  'x': 'x.com', 'twitter': 'x.com',
  'fb': 'facebook.com', 'ig': 'instagram.com', 'snap': 'snapchat.com',
  'threads': 'threads.net', 'bluesky': 'bsky.app', 'bsky': 'bsky.app',
  'mastodon': 'mastodon.social', 'telegram': 'telegram.org', 'signal': 'signal.org',
  'twitch': 'twitch.tv',
  // ── Email / Google & Microsoft products ──
  'google mail': 'gmail.com', 'hotmail': 'outlook.com', 'live': 'outlook.com',
  'yahoo mail': 'yahoo.com', 'proton': 'proton.me', 'protonmail': 'proton.me',
  'proton mail': 'proton.me', 'apple id': 'appleid.apple.com',
  'google drive': 'drive.google.com', 'google docs': 'docs.google.com',
  'google photos': 'photos.google.com', 'google calendar': 'calendar.google.com',
  'google cloud': 'cloud.google.com', 'gcp': 'cloud.google.com',
  'google play': 'play.google.com', 'play store': 'play.google.com',
  'youtube music': 'music.youtube.com', 'youtube tv': 'tv.youtube.com',
  'office 365': 'office.com', 'microsoft 365': 'office.com', 'microsoft teams': 'teams.microsoft.com',
  'azure': 'azure.microsoft.com',
  // ── Finance / banking ──
  'bofa': 'bankofamerica.com', 'boa': 'bankofamerica.com', 'citibank': 'citi.com',
  'amex': 'americanexpress.com', 'charles schwab': 'schwab.com', 'td bank': 'td.com',
  'navy federal': 'navyfederal.org', 'navyfederal': 'navyfederal.org',
  'zelle': 'zellepay.com', 'cash app': 'cash.app', 'cashapp': 'cash.app',
  'quickbooks': 'quickbooks.intuit.com', 'turbotax': 'turbotax.intuit.com',
  // ── Shopping / retail ──
  'the home depot': 'homedepot.com', 'amazon prime': 'amazon.com', 'prime': 'amazon.com',
  // ── Streaming / media ──
  'disney': 'disneyplus.com', 'disney+': 'disneyplus.com',
  'hbo': 'max.com', 'hbo max': 'max.com', 'paramount': 'paramountplus.com',
  'paramount+': 'paramountplus.com', 'peacock': 'peacocktv.com',
  'apple tv': 'tv.apple.com', 'apple music': 'music.apple.com',
  'fubo': 'fubo.tv', 'fubotv': 'fubo.tv', 'tubi': 'tubitv.com',
  // ── News ──
  'new york times': 'nytimes.com',
  'wall street journal': 'wsj.com', 'the washington post': 'washingtonpost.com',
  'associated press': 'apnews.com', 'npr': 'npr.org', 'pbs': 'pbs.org',
  'the weather channel': 'weather.com',
  // ── Telecom / utilities / shipping ──
  't-mobile': 't-mobile.com', 'tmobile': 't-mobile.com',
  't mobile': 't-mobile.com', 'comcast': 'xfinity.com', 'spectrum': 'spectrum.net',
  'us postal service': 'usps.com',
  // ── Government ──
  'irs': 'irs.gov', 'ssa': 'ssa.gov', 'social security': 'ssa.gov',
  'medicare': 'medicare.gov', 'medicaid': 'medicaid.gov', 'va': 'va.gov',
  'studentaid': 'studentaid.gov', 'student aid': 'studentaid.gov', 'fafsa': 'studentaid.gov',
  'uscis': 'uscis.gov', 'cdc': 'cdc.gov', 'nih': 'nih.gov', 'dmv': 'dmv.org',
  // ── Dev / cloud / work / SaaS ──
  'aws': 'aws.amazon.com', 'amazon web services': 'aws.amazon.com',
  'bitbucket': 'bitbucket.org', 'jira': 'atlassian.com', 'confluence': 'atlassian.com',
  'zoom': 'zoom.us', 'notion': 'notion.so',
  // ── Gaming ──
  'steam': 'steampowered.com', 'epic': 'epicgames.com', 'electronic arts': 'ea.com',
  'battlenet': 'battle.net',
  // ── AI / learning ──
  'claude': 'claude.ai', 'perplexity': 'perplexity.ai', 'character ai': 'character.ai',
  'google gemini': 'gemini.google.com', 'khan academy': 'khanacademy.org',
  // ── Reference / classifieds / archives ──
  'wikipedia': 'wikipedia.org', 'craigslist': 'craigslist.org',
  'archive': 'archive.org', 'internet archive': 'archive.org',
};

const HOST_RE = /^[a-z0-9-]+(\.[a-z0-9-]+)+$/i;

function normalize(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, ' ');
}

/**
 * Best-effort domain for a brand/site name, or `null` when nothing sensible
 * can be derived. On-device only. See module docs for resolution order.
 */
export function suggestDomainForName(name: string | null | undefined): string | null {
  if (!name) return null;
  const n = normalize(name);
  if (!n) return null;

  // 1. Already a domain or URL (single token with a dot, or a real scheme):
  //    return its host so a user typing "cnn.com" or a pasted URL still works.
  if (!n.includes(' ') && (n.includes('.') || /:\/\//.test(n))) {
    const parsed = tryParseUrl(n);
    const host = parsed?.hostname.replace(/^www\./, '');
    if (host && HOST_RE.test(host)) return host;
  }

  // 2. Curated brand map.
  const mapped = BRAND_DOMAINS[n];
  if (mapped) return mapped;

  // 3. Heuristic: slugify and append .com. Conservative length bounds keep
  //    one-character and absurdly-long inputs from producing junk domains.
  const slug = n.replace(/[^a-z0-9]/g, '');
  if (slug.length < 2 || slug.length > 40) return null;
  return `${slug}.com`;
}
